import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { clsx } from 'clsx';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Spinner } from '../../components/ui/Spinner';
import { verifyInvite, acceptInvite } from '../../modules/platform/api/auth';
import { showErrorToast, showSuccessToast } from '../../utils/errorHandler';
import { ROUTES } from '../../utils/constants';
import { validatePassword, validatePersonName, stripDigitsFromName } from '../../utils/validation';

const PASSWORD_HINT = 'Use 8+ characters with uppercase, lowercase, and a number';

interface InviteAcceptFormData {
  password: string;
  confirmPassword: string;
  name: string;
}

export const InviteAcceptPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [inviteInfo, setInviteInfo] = useState<{ email: string; role: string } | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
  } = useForm<InviteAcceptFormData>();

  const password = watch('password');
  const passwordError = errors.password?.message;
  const showPasswordHint = !passwordError && (passwordFocused || (password?.length ?? 0) > 0);

  useEffect(() => {
    if (!token) {
      setVerifying(false);
      return;
    }

    const verify = async () => {
      try {
        const info = await verifyInvite(token);
        setInviteInfo({ email: info.email, role: info.role });
      } catch (error) {
        showErrorToast(error);
      } finally {
        setVerifying(false);
      }
    };

    verify();
  }, [token]);

  const onSubmit = async (data: InviteAcceptFormData) => {
    if (!token) {
      showErrorToast(new Error('Invite token is missing'));
      return;
    }

    if (data.password !== data.confirmPassword) {
      showErrorToast(new Error('Passwords do not match'));
      return;
    }

    setIsLoading(true);
    try {
      await acceptInvite({
        token,
        password: data.password,
        name: data.name,
      });
      // After accepting invite, redirect user to login page to sign in.
      showSuccessToast('Account created — please sign in');
      const tenantId = searchParams.get('tenant_id');
      const params = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
      navigate(`${ROUTES.LOGIN}${params}`, { replace: true });
    } catch (error) {
      showErrorToast(error);
    } finally {
      setIsLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!token || !inviteInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-gray-900 dark:to-gray-800 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <p className="text-center text-error-600">
              Invalid or expired invite link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-gray-900 dark:to-gray-800 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl text-center">Accept Invitation</CardTitle>
          <p className="text-center text-gray-600 dark:text-gray-400 mt-2">
            You've been invited as a {inviteInfo.role.replace('_', ' ').toLowerCase()}
          </p>
          <p className="text-center text-sm text-gray-500 dark:text-gray-500 mt-1">
            {inviteInfo.email}
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Controller
              name="name"
              control={control}
              rules={{
                required: 'Name is required',
                validate: (value) => {
                  const result = validatePersonName(value ?? '');
                  return result.valid || result.message;
                },
              }}
              render={({ field }) => (
                <Input
                  label="Full Name"
                  {...field}
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const sanitized = stripDigitsFromName(e.target.value);
                    field.onChange(sanitized);
                  }}
                  error={errors.name?.message}
                  placeholder="Enter your full name"
                />
              )}
            />

            <div className="w-full">
              <label htmlFor="invite-password" className="block text-sm font-medium text-app-text-muted dark:text-gray-300 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  id="invite-password"
                  type={showPassword ? 'text' : 'password'}
                  {...register('password', {
                    required: 'Password is required',
                    validate: (value) => {
                      const result = validatePassword(value);
                      return result.valid || result.message;
                    },
                  })}
                  placeholder="Create a strong password"
                  onFocus={() => setPasswordFocused(true)}
                  onBlur={() => setPasswordFocused(false)}
                  className={clsx(
                    'w-full px-4 py-2 pr-12 border rounded-lg transition-colors duration-200',
                    'bg-app-surface dark:bg-gray-800 border-app-border dark:border-gray-700 text-app-text dark:text-white',
                    passwordError
                      ? 'border-error-500 focus:ring-error-500'
                      : 'border-app-border dark:border-gray-600',
                    'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent'
                  )}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-0 top-0 bottom-0 w-12 z-10 flex items-center justify-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {passwordError && (
                <p className="mt-1 text-sm text-error-600 dark:text-error-400">{passwordError}</p>
              )}
              {showPasswordHint && (
                <p className="mt-1 text-sm text-app-text-muted dark:text-gray-400">{PASSWORD_HINT}</p>
              )}
            </div>

            <div className="w-full">
              <label htmlFor="invite-confirm-password" className="block text-sm font-medium text-app-text-muted dark:text-gray-300 mb-1">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  id="invite-confirm-password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  {...register('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: (value) => value === password || 'Passwords do not match',
                  })}
                  placeholder="Confirm your password"
                  className={clsx(
                    'w-full px-4 py-2 pr-12 border rounded-lg transition-colors duration-200',
                    'bg-app-surface dark:bg-gray-800 border-app-border dark:border-gray-700 text-app-text dark:text-white',
                    errors.confirmPassword
                      ? 'border-error-500 focus:ring-error-500'
                      : 'border-app-border dark:border-gray-600',
                    'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent'
                  )}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  className="absolute right-0 top-0 bottom-0 w-12 z-10 flex items-center justify-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.confirmPassword?.message && (
                <p className="mt-1 text-sm text-error-600 dark:text-error-400">{errors.confirmPassword.message}</p>
              )}
            </div>

            <Button type="submit" className="w-full" isLoading={isLoading}>
              Accept Invitation
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

